import type { ChatCostTotals, ChatStatusFrameOutput } from '../chat';
import type { ChatTriggerSource } from '../api-schema/chat';

/** Serialized conversation for broadcast - matches client ChatRow/ChatMessageRow/ChatActionRow shape */
export type SerializedConversationSnapshot = {
  chat: {
    id: string;
    org_id: string;
    /** Org that owns the chat for credential/cross-org purposes. Derived from resource/folder links when in a folder. */
    owner_org_id?: string;
    actor_id: string;
    agent: string | null;
    agent_id: string | null;
    title: string;
    status: 'active' | 'archived';
    metadata: { loadedToolIds?: string[] } | null;
    created_at: string;
    updated_at: string;
  };
  messages: Array<{
    id: string;
    org_id: string;
    actor_id: string;
    chat_id: string;
    message_id: string | null;
    position: number;
    role: string;
    message: unknown;
    usage: unknown;
    type: string;
    read_at: string | null;
    created_at: string;
    updated_at: string;
  }>;
  actions: Array<{
    id: string;
    org_id: string;
    chat_id: string;
    type: string;
    status: string;
    data: unknown;
    completed_at: string | null;
    approved_at: string | null;
    approved_by: string | null;
    created_at: string;
    updated_at: string;
  }>;
  children?: SerializedConversationSnapshot[];
};

/**
 * Broadcast event types sent over WebSocket.
 * Keep in sync with server event-types.ts and broadcast-handler mappings.
 */
/** Serialized action for broadcast - matches snapshot.actions item shape */
export type SerializedAction =
  SerializedConversationSnapshot['actions'][number];

export type { ResourceType as ResourceUpdatedResourceType } from '../resource/resource.types';

export const BroadcastEventType = {
  ChatCreated: 'chat-created',
  ChatActionUpdated: 'chat-action-updated',
  /** Parent (or leaf) chat: child artifacts changed; payload may include full conversation. */
  ArtifactCreated: 'artifact-created',
  CreditBalanceUpdated: 'credit-balance-updated',
  RecentChatsUpdated: 'recent-chats-updated',
  ResourceUpdated: 'resource-updated',
  /** Tool provider credentials added/deleted/(dis)connected for an org. */
  ToolCredentialUpdated: 'tool-credential-updated',
  /** Slack or Linear chat bot channel/team agent mapping changed. */
  ChatBotConfigUpdated: 'chat-bot-config-updated',
  StackFrameWritten: 'stack-frame-written',
  CliConnectionStatusChanged: 'cli-connection-status-changed',
  /** Imperative workflow run progress (pg_notify only, not a domain EventType). */
  RunWorkflow: 'run-workflow',
  RunWorkflowStep: 'run-workflow-step',
  /** CSV import progress (pg_notify only, not a domain EventType). */
  DatasetImport: 'dataset-import',
} as const;

export type BroadcastEventTypeValue =
  (typeof BroadcastEventType)[keyof typeof BroadcastEventType];

export type ResourceUpdatedPayloadFolder = {
  orgId: string;
  resourceType: 'folder';
  /** When set, the folder that was updated; client can filter to only that folder. */
  resourceId?: string;
  /** Augmented: not used for folder type. */
  folderId?: string | null;
  /**
   * Set when the update is an item being filed into (or out of) this folder,
   * so list consumers can ignore placements of items they never show.
   * `triggerSource` is augmented for chats; null when unknown.
   */
  movedEntity?: {
    entityType: string;
    entityId: string;
    triggerSource?: ChatTriggerSource | null;
  };
};

/**
 * Chat resource updates over `resource-updated`.
 * When `conversationAncestry` is set, this is a message/content sync (streaming
 * or full snapshot). Metadata-only updates (title, read state, archive, etc.)
 * omit `conversationAncestry`.
 */
export type ResourceUpdatedPayloadChat = {
  orgId: string;
  resourceType: 'chat';
  resourceId: string;
  /** Augmented: folder id the resource is in, or null if not in a folder. */
  folderId?: string | null;
  /**
   * Augmented: the chat's trigger source, so list consumers can drop updates
   * for chats their filter never shows without a refetch. Null when unknown.
   */
  triggerSource?: ChatTriggerSource | null;
  /** When set, message/content sync — [self, parent, grandparent, ...]. */
  conversationAncestry?: string[];
  /** Position of the message written — delta broadcast when set. */
  messagePosition?: number;
  /**
   * True for a mid-stream chunk notify, false/absent for a settled update
   * (create, title, message finalize, archive, folder move).
   *
   * Chunk notifies fire per streamed message part, so anything that reacts by
   * refetching a *list* must ignore them — see
   * `ResourceUpdatedRecentChatsBroadcaster` server-side and the chat-list
   * watchables client-side. Surfaces that render the chat itself (message
   * deltas) still want them.
   */
  streaming?: boolean;
  /** Full serialized conversation when not using per-position delta (e.g. compaction). */
  conversation?: SerializedConversationSnapshot;
  /** Single message row when `messagePosition` is set (often augmented server-side). */
  messageDelta?: SerializedConversationSnapshot['messages'][number];
};

export type ResourceUpdatedPayloadAgent = {
  orgId: string;
  resourceType: 'agent';
  resourceId: string;
  /** Augmented: folder id the resource is in, or null if not in a folder. */
  folderId?: string | null;
  locked?: boolean;
  /** Full agent when augmented (avoids client refetch). */
  agent?: {
    ownerId: string;
    id: string;
    orgId: string;
    agentKey: string;
    editorState: unknown;
    model: string;
    locked: boolean;
    createdAt: Date;
    updatedAt: Date;
    title: string | null;
    description: string | null;
    emoji?: string | null;
  };
};

export type ResourceUpdatedPayloadDocument = {
  orgId: string;
  resourceType: 'document';
  resourceId: string;
  /** Augmented: folder id the resource is in, or null if not in a folder. */
  folderId?: string | null;
  locked?: boolean;
  /** Full document when augmented (avoids client refetch). */
  document?: {
    id: string;
    orgId: string;
    ownerId: string;
    editorState: unknown;
    locked: boolean;
    title: string | null;
    description: string | null;
    status: 'active' | 'archived';
    createdAt: Date;
    updatedAt: Date;
  };
};

export type DatasetDataUpdateBroadcast = {
  idempotencyKey: string;
  rowLabel?: string;
  column: string;
};

export type ResourceUpdatedPayloadDataset = {
  orgId: string;
  resourceType: 'dataset';
  resourceId: string;
  /** Augmented: folder id the resource is in, or null if not in a folder. */
  folderId?: string | null;
  /** Optional granular data update hint for cell/row-level dataset subscribers. */
  datasetDataUpdate?: DatasetDataUpdateBroadcast;
};

export type ResourceUpdatedPayloadWorkflow = {
  orgId: string;
  resourceType: 'workflow';
  resourceId: string;
  /** Augmented: folder id the resource is in, or null if not in a folder. */
  folderId?: string | null;
  /**
   * What kind of change this is. `'run'` is a high-frequency, non-content
   * lifecycle tick — a workflow run status change (dispatch, cancel, retry,
   * status sync, draft test, manual trigger) or trigger activity (install,
   * reinstall, trigger rename) that does not alter the workflow's own identity.
   * `'content'` is a create / rename / move / archive. Current servers always
   * set one of the two; an absent value means an older server that predates this
   * field, so consumers treat it as unknown, NOT as content. Content subscribers
   * (e.g. the @-mention resource list) refetch only on `'content'`, so a running
   * workflow does not drive repeated list refetches.
   */
  changeType?: 'run' | 'content';
  /** Full workflow when augmented (avoids client refetch). */
  workflow?: {
    id: string;
    orgId: string;
    ownerId: string;
    name: string;
    status: 'active' | 'archived';
    workflowStatus?: string;
    workflowType?: string;
    createdAt: Date;
    updatedAt: Date;
  };
};

export type WebhookTriggerInstallBroadcast = {
  installStatus: 'draft' | 'installing' | 'active' | 'error' | 'archived';
  installError: { code?: string; message?: string } | null;
};

export type ResourceUpdatedPayloadWorkflowTrigger = {
  orgId: string;
  resourceType: 'workflow_trigger';
  resourceId: string;
  folderId?: string | null;
  /** Augmented for webhook triggers when external install status changes. */
  webhookInstall?: WebhookTriggerInstallBroadcast;
};

/**
 * Tool provider credential change for an org (credential added, deleted, or a
 * provider connected/disconnected). Clients refresh tool caches on this so
 * tool-aware UI (e.g. @-mentions) reflects the new connection state.
 */
export type ToolCredentialUpdatedPayload = {
  orgId: string;
  /** When set, the provider whose credentials changed; clients may filter on it. */
  provider?: string;
};

export type ChatBotConfigUpdatedPayload = {
  orgId: string;
  bot: 'slack' | 'linear' | 'teams';
};

export type ResourceUpdatedPayload =
  | ResourceUpdatedPayloadFolder
  | ResourceUpdatedPayloadChat
  | ResourceUpdatedPayloadAgent
  | ResourceUpdatedPayloadDocument
  | ResourceUpdatedPayloadDataset
  | ResourceUpdatedPayloadWorkflow
  | ResourceUpdatedPayloadWorkflowTrigger;

export type BroadcastEventPayloadMap = {
  [BroadcastEventType.ChatCreated]: { chatId: string };
  [BroadcastEventType.RecentChatsUpdated]: Record<string, never>;
  [BroadcastEventType.CreditBalanceUpdated]: { orgId: string };
  [BroadcastEventType.ChatActionUpdated]: {
    chatId: string;
    conversationAncestry?: string[];
    action: SerializedAction;
  };
  [BroadcastEventType.ArtifactCreated]: {
    orgId: string;
    chatId: string;
    /** [self, parent, grandparent, ...] — chat where the artifact was written is first. */
    conversationAncestry?: string[];
    conversation?: SerializedConversationSnapshot;
  };
  [BroadcastEventType.ResourceUpdated]: ResourceUpdatedPayload;
  [BroadcastEventType.ToolCredentialUpdated]: ToolCredentialUpdatedPayload;
  [BroadcastEventType.ChatBotConfigUpdated]: ChatBotConfigUpdatedPayload;
  [BroadcastEventType.StackFrameWritten]: {
    runKey: string;
    orgId: string;
    /** Event type: start, success, error, fail, retry, cancel, allocated, queued, or 'created' for new frame */
    eventType: string;
    conversationId?: string;
    parentConversationId?: string;
    /** [self, parent, grandparent, ...] - conversation IDs from leaf to root. Enables client filter for n-level deep hierarchy. */
    conversationAncestry?: string[];
    /** Populated by WebSocketBroadcastService when consuming pg_notify - avoids client fetch */
    stackFrames?: ChatStatusFrameOutput[];
    /** Populated when parentConversationId is set - for parent view */
    parentStackFrames?: ChatStatusFrameOutput[];
    /** Lifetime cost totals for the conversation; mirrors `stackFrames`. */
    totals?: ChatCostTotals;
    /** Lifetime cost totals for the parent conversation; mirrors `parentStackFrames`. */
    parentTotals?: ChatCostTotals;
  };
  [BroadcastEventType.CliConnectionStatusChanged]: {
    userId: string;
    connected: boolean;
    timestamp: string;
  };
  [BroadcastEventType.RunWorkflow]: {
    workflowId: string;
    runId: string;
    status: string;
    finishedAt: string | null;
    /**
     * Set only for runs a dataset trigger produced. Carrying the row identity
     * in the payload is the point of this event: `resource-updated` says only
     * "this workflow changed", so a dataset grid would have to refetch its
     * whole overlay on every step of every run to find out what moved.
     */
    datasetId?: string;
    datasetRowId?: string | null;
    triggerId?: string;
    /**
     * Columns the run wrote back, when it wrote any and they fit.
     *
     * Absent means "unknown", not "none" -- `emitBroadcastNotify` silently
     * drops payloads over 8KB, so oversized outputs are stripped here and the
     * receiver refetches that one row instead of losing the update entirely.
     */
    outputs?: Record<string, unknown>;
    /**
     * The batch this run belongs to, when something grouped it.
     *
     * Carried so a progress bar can move without polling: a page already
     * subscribes to this event for the run overlay, and every terminal run in a
     * batch produces one, so the batch's progress is derivable from the stream
     * a page is already receiving. Absent for an ungrouped run.
     */
    groupId?: string;
  };
  [BroadcastEventType.RunWorkflowStep]: {
    workflowId: string;
    runId: string;
    nodeId: string;
  };
  [BroadcastEventType.DatasetImport]: {
    orgId: string;
    datasetId: string;
    /**
     * The import this describes. A dataset can be re-uploaded while an earlier
     * import is still finishing, and a progress screen must not be moved along
     * by an import it did not start.
     */
    importId: string;
    phase: DatasetImportPhase;
    /** Set only on `failed`. */
    error?: string;
  };
};

/**
 * How far a CSV import has got.
 *
 * Phase transitions rather than a percentage: after the bytes land there is one
 * DuckDB `COPY` with no incremental callback, so any number between "started"
 * and "finished" would be invented. These are the boundaries the saga actually
 * crosses, in order.
 */
export const DATASET_IMPORT_PHASES = [
  /** Bytes are in staging storage and the saga has picked the work up. */
  'staged',
  /** Reading the CSV and writing the parquet artifact. */
  'parsing',
  /** Recording which rows the new artifact holds. */
  'indexing',
  /** Rows are queryable. */
  'ready',
  'failed',
] as const;

export type DatasetImportPhase = (typeof DATASET_IMPORT_PHASES)[number];

export type BroadcastEvent = {
  [K in keyof BroadcastEventPayloadMap]: {
    type: K;
    payload: BroadcastEventPayloadMap[K];
  };
}[keyof BroadcastEventPayloadMap];
